import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Footer from '../components/Footer';
import {
  CheckCircle2, XCircle, RefreshCw, ShieldCheck, QrCode, Lock, Loader2,
} from 'lucide-react';

interface RedeemInfo {
  redeemCode: string;
  status: 'claimed' | 'redeemed' | 'expired';
  restaurantName: string | null;
  offerTitle: string | null;
  region: 'local' | 'mainland';
  validUntil: number | null;
  redeemInstructions: string | null;
  claimedAt: number;
  redeemedAt: number | null;
  expired: boolean;
  locked: boolean;
  attemptsRemaining: number;
  verifyConfigured: boolean;
  myLink?: string | null;
  merchantId?: number | null;
}

interface RedeemResult {
  restaurantName: string;
  offerTitle: string;
  redeemCode: string;
  redeemedAt: number;
}

const POLL_INTERVAL_MS = 3000;

// Companion consent card — the redemption moment is the highest-intent
// touchpoint; one tap opts into this restaurant's future offers (PDPO evidence
// with timestamp via marketing_consents). Never blocks the QR flow.
function ConsentCard({ info }: { info: RedeemInfo }) {
  const [state, setState] = useState<'idle' | 'done' | 'off'>('idle');
  const token = info.myLink ? info.myLink.split('/').pop() || '' : '';

  if (!token || !info.merchantId || info.status !== 'claimed') return null;

  const enable = async () => {
    try {
      const res = await fetch('/api/public/me/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, merchantId: info.merchantId, on: true }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '失敗');
      setState('done');
    } catch {
      setState('off');
    }
  };

  if (state === 'done') {
    return (
      <div className="mt-5 pt-4 border-t border-stone-100 dark:border-gray-700 text-center">
        <p className="text-sm font-bold text-green-600">🔔 已開啟通知！</p>
        <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
          {info.restaurantName}有新優惠會 WhatsApp 話你知。可隨時在你的
          <a href={info.myLink || '#'} className="text-x1493-primary font-bold hover:underline">優惠中心</a>關閉。
        </p>
      </div>
    );
  }

  return (
    <div className="mt-5 pt-4 border-t border-stone-100 dark:border-gray-700 text-center">
      {state === 'off' ? (
        <p className="text-xs text-stone-400">網絡不穩，稍後可在你的優惠中心開啟通知。</p>
      ) : (
        <>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            想 {info.restaurantName} 有新優惠時 WhatsApp 話你知？
          </p>
          <button
            onClick={enable}
            className="mt-2 px-5 py-2 rounded-xl bg-x1493-primary text-white text-sm font-bold hover:bg-red-700 transition inline-flex items-center gap-1.5"
          >
            🔔 開啟通知（免費）
          </button>
          <p className="text-[10px] text-stone-400 mt-1.5">隨時可關閉，唔影響你使用優惠</p>
        </>
      )}
    </div>
  );
}

function formatDate(ts?: number | null) {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleString('zh-HK', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

export default function Redeem() {
  const { code = '' } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const merchantMode = searchParams.get('m') === '1';

  const [info, setInfo] = useState<RedeemInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [verifyInput, setVerifyInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<RedeemResult | null>(null);
  const [shakeKey, setShakeKey] = useState(0);

  const infoRef = useRef<RedeemInfo | null>(null);
  infoRef.current = info;

  const fetchInfo = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await fetch(`/api/public/offers/redeem/${encodeURIComponent(code)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoadError(data.error || '載入失敗，請重新開啟連結');
        setInfo(null);
      } else {
        setLoadError(null);
        setInfo(data.data || data);
      }
    } catch {
      if (!silent) setLoadError('網絡不穩，請重新整理');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [code]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  // Customer mode: poll while awaiting staff redemption
  useEffect(() => {
    if (merchantMode || info?.status !== 'claimed') return;
    const timer = window.setInterval(() => {
      const current = infoRef.current;
      if (current?.status !== 'claimed') return;
      fetch('/api/public/offers/redeem/' + encodeURIComponent(current.redeemCode))
        .then((r) => r.json())
        .then((data) => {
          if (data?.data?.status && data.data.status !== 'claimed') {
            setInfo(data.data as RedeemInfo);
          }
        })
        .catch(() => { /* silent */ });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [merchantMode, info?.status]);

  const submitRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/public/offers/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: info.redeemCode, verifyCode: verifyInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data.error || '核銷失敗，請重試');
        setShakeKey((k) => k + 1);
        if (data.attemptsRemaining !== undefined || res.status === 423) {
          await fetchInfo(true);
        }
        if (res.status === 409) {
          await fetchInfo(true);
        }
        return;
      }
      setResult(data.data || data);
      setVerifyInput('');
    } catch {
      setSubmitError('網絡不穩，請重試');
      setShakeKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const redeemUrl = info ? `${window.location.origin}/redeem/${info.redeemCode}?m=1` : '';

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-x1493-dark flex flex-col items-center px-4 py-6">
      <style>{`
        @keyframes redeem-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
        .redeem-shake { animation: redeem-shake 0.4s ease-in-out; }
        @keyframes redeem-pop { 0%{transform:scale(0.5);opacity:0} 70%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
        .redeem-pop { animation: redeem-pop 0.45s cubic-bezier(0.34,1.56,0.64,1) both; }
      `}</style>

      {/* Brand header */}
      <div className="w-full max-w-md flex items-center justify-between mb-5">
        <a href="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="香港辣谷" className="w-8 h-8 rounded-full" />
          <span className="font-bold text-stone-900 dark:text-white">香港辣谷</span>
        </a>
        {merchantMode && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            <ShieldCheck size={13} /> 店員核銷模式
          </span>
        )}
      </div>

      <div className="w-full max-w-md flex-1">

        {/* ---------- Loading ---------- */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-stone-400">
            <Loader2 className="w-10 h-10 animate-spin" />
            <p className="mt-3 text-sm">載入中…</p>
          </div>
        )}

        {/* ---------- Load error / not found ---------- */}
        {!loading && loadError && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-stone-200 dark:border-gray-700 p-8 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-bold text-stone-900 dark:text-white mb-4">{loadError}</p>
            <button
              onClick={() => { setLoading(true); fetchInfo(); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-x1493-primary text-white rounded-xl font-bold hover:bg-red-700 transition"
            >
              <RefreshCw size={16} /> 重新載入
            </button>
          </div>
        )}

        {/* ---------- Merchant mode ---------- */}
        {!loading && !loadError && info && merchantMode && (result || info.status === 'redeemed' ? (
          <MerchantSuccessView
            restaurantName={result?.restaurantName || info.restaurantName || ''}
            offerTitle={result?.offerTitle || info.offerTitle || ''}
            redeemCode={info.redeemCode}
            redeemedAt={result?.redeemedAt || info.redeemedAt || undefined}
          />
        ) : info.status === 'expired' || info.expired ? (
          <MerchantBlockedView
            icon={<XCircle className="w-14 h-14 text-gray-400 mx-auto mb-4" />}
            title="優惠已過期"
            description="此優惠券已過有效期，無法核銷。"
          />
        ) : info.locked ? (
          <MerchantBlockedView
            icon={<Lock className="w-14 h-14 text-red-500 mx-auto mb-4" />}
            title="兌換碼已鎖定"
            description="驗證碼錯誤次數過多，請聯絡香港辣谷客服解鎖。"
          />
        ) : !info.verifyConfigured ? (
          <MerchantBlockedView
            icon={<ShieldCheck className="w-14 h-14 text-amber-500 mx-auto mb-4" />}
            title="未啟用自助核銷"
            description="此優惠未設定商家驗證碼，請聯絡香港辣谷以其他方式確認優惠。"
          />
        ) : (
          <form onSubmit={submitRedeem} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-stone-200 dark:border-gray-700 p-6">
            <div className="text-center mb-6">
              <p className="text-xs text-stone-400 mb-1">即將核銷</p>
              <h1 className="text-xl font-bold text-stone-900 dark:text-white">{info.restaurantName}</h1>
              {info.offerTitle && <p className="text-stone-500 dark:text-stone-300 mt-1">{info.offerTitle}</p>}
              <p className="inline-block mt-3 font-mono font-bold text-stone-700 dark:text-stone-200 bg-stone-100 dark:bg-gray-700 px-3 py-1 rounded-lg">
                {info.redeemCode}
              </p>
            </div>

            <div className="border-t border-stone-100 dark:border-gray-700 pt-6">
              <label className="block text-center text-sm font-semibold text-stone-700 dark:text-stone-200 mb-3">
                輸入商家驗證碼（公司電話最後 4 位）
              </label>
              <div key={shakeKey} className={shakeKey > 0 ? 'redeem-shake' : ''}>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={4}
                  value={verifyInput}
                  onChange={(e) => setVerifyInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="0000"
                  autoFocus
                  className="w-full text-center text-4xl font-bold tracking-[0.6em] px-4 py-4 border-2 border-stone-300 dark:border-gray-600 rounded-2xl outline-none bg-white dark:bg-gray-800 dark:text-white focus:border-x1493-primary placeholder:text-stone-300 dark:placeholder:text-gray-600"
                />
              </div>

              {submitError && (
                <p className="mt-3 text-center text-sm font-medium text-red-600 dark:text-red-400">{submitError}</p>
              )}
              {info.attemptsRemaining < 5 && !submitError && (
                <p className="mt-3 text-center text-xs text-amber-600 dark:text-amber-400">
                  剩餘 {info.attemptsRemaining} 次嘗試機會
                </p>
              )}

              <button
                type="submit"
                disabled={verifyInput.length !== 4 || submitting}
                className="w-full mt-5 py-3.5 bg-x1493-primary text-white rounded-xl font-bold text-lg hover:bg-red-700 transition disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 size={20} />}
                {submitting ? '核銷中…' : '確認兌換'}
              </button>
              <p className="mt-3 text-center text-xs text-stone-400">
                確認前請先核對顧客優惠內容。顧客會即時收到 WhatsApp 兌換確認。
              </p>
            </div>
          </form>
        ))}

        {/* ---------- Customer mode ---------- */}
        {!loading && !loadError && info && !merchantMode && (info.status === 'redeemed' ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-green-200 dark:border-green-800 p-8 text-center redeem-pop">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-stone-900 dark:text-white mb-2">已成功兌換 🎉</h1>
            <p className="text-stone-600 dark:text-stone-300">
              【{info.restaurantName}】{info.offerTitle}
            </p>
            {info.redeemedAt && (
              <p className="text-sm text-stone-400 mt-2">兌換時間：{formatDate(info.redeemedAt)}</p>
            )}
            <p className="text-sm text-stone-500 dark:text-stone-400 mt-4">
              我哋已透過 WhatsApp 發送兌換確認訊息俾你，多謝支持香港辣谷 🌶️
            </p>
            <a href="/offers" className="inline-block mt-6 px-6 py-3 bg-x1493-primary text-white rounded-xl font-bold hover:bg-red-700 transition">
              睇更多食肆優惠
            </a>
          </div>
        ) : info.status === 'expired' || info.expired ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-stone-200 dark:border-gray-700 p-8 text-center">
            <XCircle className="w-14 h-14 text-gray-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-stone-900 dark:text-white mb-2">優惠已過期</h1>
            <p className="text-stone-500 dark:text-stone-300 text-sm">
              【{info.restaurantName}】{info.offerTitle}
              {info.validUntil ? `（有效期至 ${formatDate(info.validUntil)}）` : ''}
            </p>
            <a href="/offers" className="inline-block mt-6 px-6 py-3 bg-x1493-primary text-white rounded-xl font-bold hover:bg-red-700 transition">
              睇更多食肆優惠
            </a>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-stone-200 dark:border-gray-700 p-6 text-center">
            <div className="mb-4">
              <h1 className="text-lg font-bold text-stone-900 dark:text-white">{info.restaurantName}</h1>
              {info.offerTitle && <p className="text-stone-500 dark:text-stone-300 mt-1">{info.offerTitle}</p>}
              {info.validUntil && (
                <p className="text-xs text-stone-400 mt-1">有效期至 {formatDate(info.validUntil)}</p>
              )}
            </div>

            {info.verifyConfigured ? (
              <>
                <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-700 dark:text-stone-200 bg-stone-100 dark:bg-gray-700 px-3 py-1.5 rounded-full mb-4">
                  <QrCode size={15} /> 結帳時將此畫面俾店員掃描
                </p>
                <div className="mx-auto w-fit p-4 bg-white rounded-2xl border-4 border-stone-900 dark:border-gray-200 shadow-lg">
                  <QRCodeSVG value={redeemUrl} size={248} level="M" />
                </div>
                <p className="font-mono font-bold text-lg text-stone-700 dark:text-stone-200 mt-4 tracking-wider">
                  {info.redeemCode}
                </p>
                <p className="text-xs text-stone-400 mt-2 flex items-center justify-center gap-1">
                  <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                  此頁面會自動更新，兌換成功後會即時顯示
                </p>
            {info.redeemInstructions && (
              <div className="mt-5 pt-4 border-t border-stone-100 dark:border-gray-700">
                <p className="text-xs font-semibold text-stone-500 dark:text-stone-400 mb-1">兌換說明</p>
                <p className="text-sm text-stone-600 dark:text-stone-300 whitespace-pre-line">{info.redeemInstructions}</p>
              </div>
            )}

            {/* Consent touchpoint — companion to the QR, never blocks redemption */}
            <ConsentCard info={info} />
          </>
        ) : (
              <div className="pt-2">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="font-mono font-bold text-lg text-stone-700 dark:text-stone-200">{info.redeemCode}</p>
                <p className="text-sm text-stone-600 dark:text-stone-300 mt-3 whitespace-pre-line">
                  {info.redeemInstructions || '出示此訊息同兌換碼畀店員，即可享用優惠。'}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-stone-400 dark:text-stone-500 mt-6 text-center">
        香港辣谷 HKSpicyValley · hkspicyvalley.com
      </p>
      <Footer />
    </div>
  );
}

function MerchantSuccessView({
  restaurantName, offerTitle, redeemCode, redeemedAt,
}: { restaurantName: string; offerTitle: string; redeemCode: string; redeemedAt?: number }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-green-200 dark:border-green-800 p-8 text-center redeem-pop">
      <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-stone-900 dark:text-white mb-2">✓ 兌換成功</h1>
      <p className="text-stone-600 dark:text-stone-300">
        【{restaurantName}】{offerTitle}
      </p>
      <p className="font-mono text-sm text-stone-500 dark:text-stone-400 mt-2">{redeemCode}</p>
      {redeemedAt && <p className="text-sm text-stone-400 mt-1">{formatDate(redeemedAt)}</p>}
      <p className="text-sm text-stone-500 dark:text-stone-400 mt-4">
        顧客會即時收到 WhatsApp 兌換確認訊息。
      </p>
    </div>
  );
}

function MerchantBlockedView({
  icon, title, description,
}: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-stone-200 dark:border-gray-700 p-8 text-center">
      {icon}
      <h1 className="text-xl font-bold text-stone-900 dark:text-white mb-2">{title}</h1>
      <p className="text-stone-500 dark:text-stone-300 text-sm">{description}</p>
    </div>
  );
}
